import React, { useState } from 'react';
import { X, Code, Sparkles, Loader2, Save, Eye } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { slidesService } from '../../services/slidesService';
import HtmlSlide from './HtmlSlide';

const HtmlSlideEditor = ({ bookId, deckId, existingSlide, onSave, onClose }) => {
    const [activeTab, setActiveTab] = useState('paste');
    const [htmlContent, setHtmlContent] = useState(existingSlide?.htmlContent || '');
    const [prompt, setPrompt] = useState('');
    const [generating, setGenerating] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const handleGenerate = async () => {
        if (!prompt.trim()) return;
        setGenerating(true);
        setError(null);
        try {
            const generateSlideHtml = httpsCallable(functions, 'generateSlideHtml');
            const result = await generateSlideHtml({ prompt, bookId });
            if (result.data?.htmlContent) {
                setHtmlContent(result.data.htmlContent);
                setActiveTab('paste');
            }
        } catch (err) {
            console.error('Generation failed:', err);
            setError(err.message || 'Failed to generate HTML');
        } finally {
            setGenerating(false);
        }
    };

    const handleSave = async () => {
        if (!htmlContent.trim()) return;
        setSaving(true);
        setError(null);
        try {
            if (existingSlide?.id) {
                await slidesService.updateSlide(bookId, deckId, existingSlide.id, {
                    htmlContent
                });
            } else {
                const currentSlides = await slidesService.getSlides(bookId, deckId);
                await slidesService.addSlide(bookId, deckId, {
                    type: 'html',
                    htmlContent,
                    order: currentSlides.length,
                    interactions: []
                });
            }
            onSave?.();
        } catch (err) {
            console.error('Save failed:', err);
            setError(err.message || 'Failed to save slide');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl w-full max-w-6xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-violet-600 to-indigo-600 text-white">
                    <div className="flex items-center gap-3">
                        <Code className="h-5 w-5" />
                        <h3 className="text-lg font-bold">
                            {existingSlide ? 'Edit HTML Slide' : 'Create HTML Slide'}
                        </h3>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition-colors">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex border-b bg-gray-50">
                    <button
                        onClick={() => setActiveTab('paste')}
                        className={`flex items-center gap-2 px-6 py-3 text-sm font-semibold transition-colors ${activeTab === 'paste'
                            ? 'text-indigo-600 border-b-2 border-indigo-600 bg-white'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        <Code className="w-4 h-4" />
                        Paste HTML
                    </button>
                    <button
                        onClick={() => setActiveTab('ai')}
                        className={`flex items-center gap-2 px-6 py-3 text-sm font-semibold transition-colors ${activeTab === 'ai'
                            ? 'text-indigo-600 border-b-2 border-indigo-600 bg-white'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        <Sparkles className="w-4 h-4" />
                        Generate with AI
                    </button>
                </div>

                {/* Error */}
                {error && (
                    <div className="mx-4 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
                        <span>{error}</span>
                        <button onClick={() => setError(null)} className="ml-auto p-1 hover:bg-red-100 rounded">
                            <X className="w-3 h-3" />
                        </button>
                    </div>
                )}

                {/* Content */}
                <div className="flex-1 overflow-hidden">
                    {activeTab === 'paste' ? (
                        <div className="flex h-full" style={{ minHeight: '400px' }}>
                            {/* Editor */}
                            <div className="flex-1 flex flex-col border-r">
                                <div className="px-4 py-2 bg-gray-50 border-b text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    HTML Code
                                </div>
                                <textarea
                                    value={htmlContent}
                                    onChange={(e) => setHtmlContent(e.target.value)}
                                    placeholder="Paste your HTML code here..."
                                    className="flex-1 w-full p-4 font-mono text-sm resize-none focus:outline-none bg-gray-900 text-green-400"
                                    spellCheck={false}
                                />
                            </div>

                            {/* Preview */}
                            <div className="flex-1 flex flex-col">
                                <div className="px-4 py-2 bg-gray-50 border-b text-xs font-medium text-gray-500 uppercase tracking-wider flex items-center gap-2">
                                    <Eye className="w-3 h-3" />
                                    Live Preview
                                </div>
                                <div className="flex-1 p-4 bg-gray-100 flex items-center justify-center">
                                    <div className="w-full aspect-video bg-white rounded-lg shadow overflow-hidden">
                                        <HtmlSlide htmlContent={htmlContent} className="w-full h-full" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="p-6 space-y-4" style={{ minHeight: '400px' }}>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Describe the slide you want to create
                                </label>
                                <textarea
                                    value={prompt}
                                    onChange={(e) => setPrompt(e.target.value)}
                                    placeholder="e.g., A title slide about Machine Learning with a gradient background and bullet points..."
                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                                    rows={4}
                                />
                            </div>
                            <button
                                onClick={handleGenerate}
                                disabled={generating || !prompt.trim()}
                                className="flex items-center gap-2 px-6 py-3 bg-violet-600 text-white rounded-xl font-bold hover:bg-violet-700 disabled:opacity-50 transition-colors"
                            >
                                {generating ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Generating...
                                    </>
                                ) : (
                                    <>
                                        <Sparkles className="w-4 h-4" />
                                        Generate Slide
                                    </>
                                )}
                            </button>

                            {/* Preview of generated content */}
                            {htmlContent && (
                                <div>
                                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                                        <Eye className="w-3 h-3" />
                                        Generated Preview
                                    </div>
                                    <div className="w-full aspect-video bg-white rounded-lg shadow overflow-hidden">
                                        <HtmlSlide htmlContent={htmlContent} className="w-full h-full" />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-6 py-2.5 text-gray-700 font-medium hover:bg-gray-200 rounded-xl transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving || !htmlContent.trim()}
                        className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                    >
                        {saving ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Saving...
                            </>
                        ) : (
                            <>
                                <Save className="w-4 h-4" />
                                Save Slide
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default HtmlSlideEditor;
